import {
  HANDLE_BRANDS_BY_KIND,
  HANDLE_COLORS_BY_KIND,
  HANDLE_SHAPES,
  HOOK_SUBCATEGORIES,
  type ApiClientErrorCode,
  type HandleKind,
  type HandleListItem,
} from '@pomoshnik/shared';
import { Header } from '@/components/Header/Header';
import { BottomBar } from '@/components/BottomBar/BottomBar';
import { FilterChips } from './FilterChips';
import { ResultCards } from './ResultCards';

/**
 * Presenter для Экрана 2.4.2 (Каталог ручек/крючков). См. ADR-0005.
 *
 * Чистая функция от props к JSX. Никаких useEffect / fetch / useNavigate.
 * 5 состояний (P-021): idle / loading / success / empty / error.
 *
 * Локальный state есть только для предварительного выбора чипов до клика «Найти»
 * (selectedColors, selectedShapes управляются Container'ом, передаются как props).
 */
export type HandlesCatalogState = 'idle' | 'loading' | 'success' | 'empty' | 'error';

export interface HandlesCatalogViewProps {
  kind: HandleKind;
  state: HandlesCatalogState;
  // Только при state='success' / 'empty'
  items?: readonly HandleListItem[];
  // Ф-Фронт (ADR-0070): map `photoKey → signed-URL` для фото в гриде.
  imageUrls?: Record<string, string>;
  // Только при state='error'
  error?: { code: ApiClientErrorCode; message: string };

  // Текущий выбор chip'ов (preview, до клика «Найти»)
  selectedBrands: readonly string[];
  selectedColors: readonly string[];
  selectedShapes: readonly string[];

  // Keyset-пагинация (ADR-0073): «Показать ещё».
  hasMore?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;

  // Callbacks
  onChipBrandToggle: (value: string) => void;
  onChipColorToggle: (value: string) => void;
  onChipShapeToggle: (value: string) => void;
  onResetBrands: () => void;
  onResetColors: () => void;
  onResetShapes: () => void;
  onSearch: () => void;
  onResetAll: () => void;
  onItemClick: (globalId: string) => void;
}

export function HandlesCatalogView(props: HandlesCatalogViewProps) {
  const { kind } = props;
  const title = kind === 'handle' ? 'Ручки' : 'Крючки';
  // Цвет-чипы зависят от kind (B-247): у ручек убраны 3 «крючковых» цвета с 0 SKU. См. HANDLE_COLORS_BY_KIND.
  const colorVocab = HANDLE_COLORS_BY_KIND[kind];
  const shapeVocab = kind === 'handle' ? HANDLE_SHAPES : HOOK_SUBCATEGORIES;
  const shapeLabel = kind === 'handle' ? 'Форма' : 'Подкатегория';
  // Бренд-чипы зависят от kind: REJS только у крючков (0 ручек в данных). См. HANDLE_BRANDS_BY_KIND.
  const brandVocab = HANDLE_BRANDS_BY_KIND[kind];

  return (
    <main className="min-h-screen flex flex-col bg-canvas">
      <Header title={title} />

      {/* Filter panel — всегда видна на MVP (Phase 5). Toggle-логика — Phase 7+. */}
      <section className="border-b border-icon-border p-6 flex flex-col gap-6">
        {/* ADR-0076: «Бренд» — мульти-выбор (один список для обоих kind). «Без бренда» = '' (маппинг в Container). */}
        <FilterChips
          groupLabel="Бренд"
          values={brandVocab}
          selected={props.selectedBrands}
          onToggle={props.onChipBrandToggle}
          onResetGroup={props.onResetBrands}
        />
        <FilterChips
          groupLabel="Цвет"
          values={colorVocab}
          selected={props.selectedColors}
          onToggle={props.onChipColorToggle}
          onResetGroup={props.onResetColors}
        />
        <FilterChips
          groupLabel={shapeLabel}
          values={shapeVocab}
          selected={props.selectedShapes}
          onToggle={props.onChipShapeToggle}
          onResetGroup={props.onResetShapes}
        />
        {/* Вертикальный стек (P-019). На десктопе капим ширину (sm:max-w-xs),
            чтобы кнопки не растягивались на весь full-bleed экран; на мобиле
            (<640px) — full-width как прежде. */}
        <div className="flex flex-col gap-3 sm:max-w-xs">
          <button
            type="button"
            onClick={props.onSearch}
            aria-label={`Найти ${title.toLowerCase()} по выбранным фильтрам`}
            className={[
              'w-full min-h-tap rounded-lg bg-cta border-b-2 border-cta-shadow text-ink',
              'font-bold text-[18px] py-3',
              'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90',
            ].join(' ')}
          >
            Найти
          </button>
          <button
            type="button"
            onClick={props.onResetAll}
            aria-label="Сбросить все фильтры"
            className={[
              'w-full min-h-tap rounded-lg bg-card border-[1.5px] border-accent text-ink',
              'text-[16px] py-3',
              'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90',
            ].join(' ')}
          >
            Сбросить фильтр
          </button>
        </div>
      </section>

      {/* Results area — switch on state */}
      <section className="flex-1 p-4 overflow-y-auto">
        <StateView {...props} />
      </section>

      <BottomBar />
    </main>
  );
}

function StateView(props: HandlesCatalogViewProps) {
  switch (props.state) {
    case 'idle':
      return <IdleState />;
    case 'loading':
      return <LoadingState />;
    case 'success':
      return (
        <>
          <p className="text-muted text-base mb-2">Найдено: {props.items?.length ?? 0}</p>
          <ResultCards
            items={props.items ?? []}
            imageUrls={props.imageUrls}
            onItemClick={props.onItemClick}
          />
          {props.hasMore && (
            <button
              type="button"
              onClick={props.onLoadMore}
              disabled={props.loadingMore}
              aria-label={props.loadingMore ? 'Загружаем ещё' : 'Показать ещё'}
              className={[
                'w-full min-h-tap rounded-lg bg-card border-[1.5px] border-accent text-ink',
                'text-[16px] py-3 mt-4 disabled:opacity-60',
                // На десктопе — не на весь full-bleed экран: капим и центрируем.
                'sm:max-w-xs sm:mx-auto sm:block',
                'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90',
              ].join(' ')}
            >
              {props.loadingMore ? 'Загружаем…' : 'Показать ещё'}
            </button>
          )}
        </>
      );
    case 'empty':
      return <EmptyState onResetAll={props.onResetAll} />;
    case 'error':
      return <ErrorState message={props.error?.message ?? ''} />;
  }
}

function IdleState() {
  return (
    <div className="flex flex-col items-center justify-center min-h-[40vh] gap-3 text-center">
      <p className="text-muted text-[18px] max-w-[260px] m-0 leading-normal">
        Выберите бренд, цвет и/или форму, затем нажмите «Найти»
      </p>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="flex flex-col items-center justify-center min-h-[40vh] gap-3 text-center">
      <p className="text-muted text-[18px] m-0">Загружаем…</p>
    </div>
  );
}

function EmptyState({ onResetAll }: { onResetAll: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[40vh] gap-4 text-center">
      <p className="text-muted text-[18px] max-w-[260px] m-0 leading-normal">
        По выбранным фильтрам ничего не найдено. Сбросьте фильтры или выберите другие.
      </p>
      <button
        type="button"
        onClick={onResetAll}
        aria-label="Сбросить все фильтры"
        className="min-h-tap rounded-lg bg-cta border-b-2 border-cta-shadow text-ink font-semibold text-base px-6 py-3"
      >
        Сбросить все фильтры
      </button>
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[40vh] gap-3 text-center">
      <p className="text-error text-[18px] max-w-[260px] m-0 leading-normal">
        Не удалось получить ответ. Проверьте интернет и попробуйте снова.
      </p>
      {message && <p className="text-muted text-sm">{message}</p>}
    </div>
  );
}
